import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";

import { formatEquivalentColors } from "../../../../tools";
import { usePricingEstimatorContext } from "../index";
import SectionPaper from "../../../widgets/SectionPaper";
import SectionTitle from "../../../widgets/SectionTitle";
import { PricingGraph, PricingGraphSkeleton } from "./PricingGraph";
import { SimilarDiamonds, SimilarDiamondsSkeleton } from "./SimilarDiamonds";

// A queried color is priced along with its confirmed equivalents: each of them contributed stones to
// the estimation. Say so, per scheme, so the numbers below are not read as
// coming from the asked-for color alone.
function EquivalenceNotice() {
  const { mutationResult } = usePricingEstimatorContext();
  const { error, loading, data } = mutationResult;

  if (loading || error) {
    return null;
  }

  // A scheme is only worth a line when its color has an equivalent.
  const schemes = (data?.estimateDiamondPrice.schemesPercentiles ?? []).filter(
    (scheme) => scheme.equivalentColors.equivalentColors.length > 0,
  );

  if (schemes.length === 0) {
    return null;
  }

  return (
    <Alert
      severity="info"
      sx={{
        my: 2,
        // The icon box stretches to the banner's height, so its own flex centers the glyph
        // against the whole message rather than leaving it pinned to the first line.
        "& .MuiAlert-icon": { alignItems: "center" },
      }}
    >
      <Stack spacing={0.5}>
        {schemes.map((scheme) => {
          // Non-empty by the filter above. The colors arrive as raw per-system values, so the
          // scheme's own system is what writes them back out.
          const otherColors = formatEquivalentColors(scheme.equivalentColors);
          return (
            <Typography
              variant="body2"
              key={`${scheme.equivalentColors.system}-${scheme.equivalentColors.color}`}
            >
              <strong>
                {scheme.equivalentColors.system} {scheme.equivalentColors.color}
              </strong>
              : diamonds of the equivalent color
              {otherColors.length > 1 ? "s" : ""}{" "}
              {otherColors.join(", ")} were also included in this pricing.
            </Typography>
          );
        })}
      </Stack>
    </Alert>
  );
}

function PricingGraphWrapper() {
  const { mutationResult } = usePricingEstimatorContext();
  const { error, loading, data } = mutationResult;
  const emptyResults =
    data?.estimateDiamondPrice.schemesPercentiles.length === 0;

  if (!loading && error) {
    return (
      <Typography variant="body2" color="error">
        Error fetching data
      </Typography>
    );
  }

  if (!loading && emptyResults) {
    return (
      <Typography variant="body2">
        No data to plot for the selected criteria
      </Typography>
    );
  }

  return (
    <Box sx={{ width: 600, height: 400, alignSelf: "center" }}>
      {loading ? <PricingGraphSkeleton /> : <PricingGraph />}
    </Box>
  );
}

function SimilarDiamondsWrapper() {
  const { mutationResult } = usePricingEstimatorContext();
  const { error, loading, data } = mutationResult;
  const emptyResults = data?.estimateDiamondPrice.similarDiamonds.length === 0;

  if (!loading && error) {
    return (
      <Typography variant="body2" color="error">
        Error fetching data
      </Typography>
    );
  }

  if (!loading && emptyResults) {
    return (
      <Typography variant="body2">
        No similar diamonds found for the selected criteria
      </Typography>
    );
  }

  return loading ? <SimilarDiamondsSkeleton /> : <SimilarDiamonds />;
}

function Results() {
  const {
    displayNormalizedPricesState,
    mutationResult,
    submittedFormValuesState,
  } = usePricingEstimatorContext();

  const [displayNormalizedPrice, setDisplayNormalizedPrice] =
    displayNormalizedPricesState;

  const selectedPricingFactors = submittedFormValuesState[0]?.factors ?? [];
  const displayNormalizedPriceToggle = selectedPricingFactors.length > 0;

  if (!mutationResult.called) {
    return null;
  }

  return (
    <>
      {/* Above both sections: the extra colors feed the percentiles and the similar diamonds alike. */}
      <EquivalenceNotice />

      <SectionPaper>
        <SectionTitle
          action={
            displayNormalizedPriceToggle && (
              <FormControlLabel
                control={
                  <Switch
                    checked={displayNormalizedPrice}
                    onChange={(_, value) => setDisplayNormalizedPrice(value)}
                  />
                }
                label="Display Normalized Price"
              />
            )
          }
        >
          Price Estimations
        </SectionTitle>
        <PricingGraphWrapper />
      </SectionPaper>

      <SectionPaper>
        <SectionTitle>Similar Diamonds</SectionTitle>
        <SimilarDiamondsWrapper />
      </SectionPaper>
    </>
  );
}

export default Results;
